import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, CheckCircle, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const getTemplateTypeName = (type) => {
    const types = {
      birthday: 'Cumpleaños',
      payment_confirmation: 'Confirmación de Pago',
      renewal_reminder: 'Recordatorio de Renovación',
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Historial de Envíos
          </h1>
          <p className="text-sm text-slate-600 mt-1">Registro de todos los correos enviados</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 border-[#e0e0e0]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-[10px]">
              <Mail className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Enviados</p>
              <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-[#e0e0e0]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-[10px]">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Exitosos</p>
              <p className="text-2xl font-bold text-green-600">
                {logs.filter(l => l.status === 'sent').length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-[#e0e0e0]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-[10px]">
              <XCircle className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Fallidos</p>
              <p className="text-2xl font-bold text-red-600">
                {logs.filter(l => l.status === 'failed').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="border-[#e0e0e0]">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Registros de Envío</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto text-slate-300 mb-4 animate-spin" />
              <p className="text-slate-600">Cargando historial...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Mail size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">No hay registros de envíos aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <Card key={log.id} className="p-4 border-[#e0e0e0]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-[10px] ${
                        log.status === 'sent' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {log.status === 'sent' ? (
                          <CheckCircle className="text-green-600" size={20} />
                        ) : (
                          <XCircle className="text-red-600" size={20} />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-[10px] font-medium">
                            {getTemplateTypeName(log.template_type)}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-[10px] font-medium ${
                            log.status === 'sent'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {log.status === 'sent' ? 'Enviado' : 'Fallido'}
                          </span>
                        </div>
                        
                        <p className="font-semibold text-slate-900">{log.subject}</p>
                        <p className="text-sm text-slate-600 mt-1">Para: {log.recipient}</p>
                        
                        {log.error_message && (
                          <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                            Error: {log.error_message}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock size={12} />
                        {formatDate(log.sent_at)}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
